import { Danger, DocumentText, ExportCurve } from '@/components/icons';
import { EmptyState } from '@/components/system';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DashboardPagination } from '@/pages/dashboard/components/DashboardPagination';
import type { PublisherResultImportsPagination } from '@/api/publisher-result-imports';
import type {
    PublisherResultImport,
    PublisherResultImportStatus,
} from '@/types/publisher-result-import';

const STATUS_LABELS: Record<PublisherResultImportStatus, string> = {
    uploaded: 'Recebido',
    queued: 'Na fila',
    extracting_text: 'Lendo PDF',
    deidentifying: 'Removendo identificação',
    extracting_tables: 'Extraindo tabelas',
    duplicate_warning: 'Possível duplicado',
    ready_for_approval: 'Pronto para revisar',
    approved: 'Aprovado',
    approved_pending_report: 'Aguardando laudo',
    failed: 'Falhou',
    cancelled: 'Cancelado',
    archived: 'Arquivado',
};

const STATUS_VARIANTS: Record<
    PublisherResultImportStatus,
    'neutral' | 'info' | 'warning' | 'success' | 'destructive' | 'brand'
> = {
    uploaded: 'neutral',
    queued: 'info',
    extracting_text: 'info',
    deidentifying: 'info',
    extracting_tables: 'info',
    duplicate_warning: 'warning',
    ready_for_approval: 'brand',
    approved: 'success',
    approved_pending_report: 'warning',
    failed: 'destructive',
    cancelled: 'neutral',
    archived: 'neutral',
};

const formatDate = (value: string) =>
    new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(value));

const getTableCount = (record: PublisherResultImport) =>
    record.extraction_json?.instruments.reduce(
        (sum, instrument) => sum + instrument.tables.length,
        0,
    ) ?? 0;

const isActionable = (status: PublisherResultImportStatus) =>
    [
        'ready_for_approval',
        'duplicate_warning',
        'uploaded',
        'queued',
        'extracting_text',
        'deidentifying',
        'extracting_tables',
    ].includes(status);

const getActionLabel = (status: PublisherResultImportStatus) => {
    if (status === 'ready_for_approval') return 'Revisar';
    if (status === 'duplicate_warning') return 'Resolver';
    return 'Acompanhar';
};

export function PublisherResultImportsPanel({
    imports,
    isLoading,
    pagination,
    currentPage,
    onImport,
    onReviewImport,
    onPageChange,
}: {
    imports: PublisherResultImport[];
    isLoading: boolean;
    pagination?: PublisherResultImportsPagination | null;
    currentPage: number;
    onImport: () => void;
    onReviewImport: (batchId: string, importId: string) => void;
    onPageChange: (page: number) => void;
}) {
    const shouldShowPagination = Boolean(pagination && pagination.totalPages > 1);

    return (
        <div className="border-b border-border/70 bg-surface/70 p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h3 className="text-sm font-semibold text-foreground">Resultados importados</h3>
                    <p className="text-sm text-muted-foreground">
                        Importe PDFs de editoras e use os resultados consolidados na elaboração do
                        laudo.
                    </p>
                </div>
                <Button type="button" variant="surface" onClick={onImport}>
                    <ExportCurve className="size-4" />
                    Importar resultado
                </Button>
            </div>

            {isLoading ? (
                <div className="mt-4 rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground">
                    Carregando importações...
                </div>
            ) : imports.length === 0 ? (
                <div className="mt-4 rounded-2xl border border-dashed border-border bg-card">
                    <EmptyState
                        title="Nenhum resultado importado"
                        description="Importe PDFs de editoras e use os resultados consolidados na elaboração do laudo."
                        icon={DocumentText}
                    />
                </div>
            ) : (
                <TooltipProvider delayDuration={150}>
                    <div className="mt-4 space-y-4">
                        <div className="grid gap-3 lg:grid-cols-2">
                            {imports.map((record) => {
                                const batchId = record.batch_id;
                                const tableCount = getTableCount(record);
                                const canOpenImport = Boolean(
                                    batchId && isActionable(record.status),
                                );
                                const actionLabel = getActionLabel(record.status);
                                const statusTooltip =
                                    record.status === 'approved' && record.approved_at
                                        ? `Aprovado em ${formatDate(record.approved_at)}`
                                        : undefined;
                                const statusBadge = (
                                    <Badge variant={STATUS_VARIANTS[record.status]}>
                                        {STATUS_LABELS[record.status]}
                                    </Badge>
                                );

                                return (
                                    <div
                                        key={record.id}
                                        className="rounded-2xl border border-border bg-card p-4 shadow-sm"
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <DocumentText className="size-4 shrink-0 text-muted-foreground" />
                                                    <p className="truncate text-sm font-semibold text-foreground">
                                                        {record.original_filename}
                                                    </p>
                                                </div>
                                                <p className="mt-1 text-xs text-muted-foreground">
                                                    {formatDate(record.created_at)}
                                                    {tableCount > 0
                                                        ? ` · ${tableCount} tabela(s)`
                                                        : ''}
                                                </p>
                                            </div>
                                            {canOpenImport && batchId ? (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() =>
                                                        onReviewImport(batchId, record.id)
                                                    }
                                                    className="shrink-0"
                                                >
                                                    {actionLabel}
                                                </Button>
                                            ) : statusTooltip ? (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <span
                                                            className="inline-flex"
                                                            aria-label={statusTooltip}
                                                        >
                                                            {statusBadge}
                                                        </span>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="top">
                                                        {statusTooltip}
                                                    </TooltipContent>
                                                </Tooltip>
                                            ) : (
                                                statusBadge
                                            )}
                                        </div>

                                        {record.safe_error_message && (
                                            <div className="mt-3 flex items-start gap-2 rounded-xl bg-red-50 p-3 text-xs text-red-700">
                                                <Danger className="mt-0.5 size-3.5 shrink-0" />
                                                {record.safe_error_message}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {shouldShowPagination && pagination ? (
                            <DashboardPagination
                                currentPage={currentPage}
                                totalPages={pagination.totalPages}
                                onPageChange={onPageChange}
                                totalReports={pagination.totalCount}
                                itemsPerPage={pagination.pageSize}
                                itemLabel="resultados"
                            />
                        ) : null}
                    </div>
                </TooltipProvider>
            )}
        </div>
    );
}
